/**
 * useCloseOpportunity - the quick Won / Lost / Cancelled buttons, shared by the
 * Opportunities tab and an account's Opportunities tab (and the GLOBAL KSA
 * tab, which reuses the Opportunities view).
 *
 * Each button asks first (ConfirmStatusModal, which says what Lost and
 * Cancelled mean), and Won then opens WonProjectSetup for the PO: Paul Q-02,
 * every route to Won asks for it. One flow, so the two tabs cannot drift
 * apart again - the account tab used to set Won straight away, with no
 * question and no PO form.
 *
 *   const closeFlow = useCloseOpportunity({ updateProject, onToast });
 *   <OpportunitiesTable onMarkWon={closeFlow.markWon} onMarkLost={closeFlow.markLost}
 *     onMarkCancelled={closeFlow.markCancelled} ... />
 *   {closeFlow.modals}
 *
 * updateProject comes from the caller's useApp() (the KSA tab hands in its
 * own), so this file reads no context itself.
 */
import { useState } from 'react';
import { ConfirmStatusModal, LOST_NOTE, CANCELLED_NOTE, NO_QUOTE_NOTE } from './ConfirmStatusModal';
import WonProjectSetup from './WonProjectSetup';

const CHOICES = {
  Won: { color: 'green' },
  // every one but Won asks why (11/3/26)
  Lost: { color: 'red', note: LOST_NOTE, askWhy: true },
  Cancelled: { color: 'grey', note: CANCELLED_NOTE, askWhy: true },
  'No Quote': { color: 'grey', note: NO_QUOTE_NOTE, askWhy: true },
};

const nameOf = (o) => (o && (o.name || o.description || (o.quoteLetter ? `Q${o.quoteLetter}` : ''))) || 'The opportunity';

export function useCloseOpportunity({ updateProject, onToast }) {
  const [target, setTarget] = useState(null); // { opp, status } while the confirmation is up
  const [wonSetup, setWonSetup] = useState(null); // the opportunity just marked Won
  const toast = (m) => {
    if (onToast) onToast(m);
  };

  const confirm = async (reason) => {
    if (!target) return;
    const { opp, status } = target;
    // A refused save used to toast "marked as Won" and open the PO form anyway.
    const ok = await updateProject(opp.id, reason ? { status, closeReason: reason } : { status });
    setTarget(null);
    if (!ok) {
      toast(`Could not mark "${nameOf(opp)}" as ${status} - try again.`);
      return;
    }
    toast(`"${nameOf(opp)}" marked as ${status}.`);
    if (status === 'Won') setWonSetup(opp);
  };

  const choice = target ? CHOICES[target.status] : null;
  const modals = (
    <>
      {target && (
        <ConfirmStatusModal
          opp={target.opp}
          label={target.status}
          color={choice.color}
          note={choice.note}
          askWhy={!!choice.askWhy}
          onClose={() => setTarget(null)}
          onConfirm={confirm}
        />
      )}
      {/* the status is already saved; the PO details are skippable */}
      {wonSetup && (
        <WonProjectSetup
          opp={wonSetup}
          onClose={() => setWonSetup(null)}
          onSave={async (patch) => {
            const ok = await updateProject(wonSetup.id, patch);
            if (ok) toast('Project details saved.');
            return !!ok;
          }}
        />
      )}
    </>
  );

  return {
    markWon: (opp) => setTarget({ opp, status: 'Won' }),
    markLost: (opp) => setTarget({ opp, status: 'Lost' }),
    markCancelled: (opp) => setTarget({ opp, status: 'Cancelled' }),
    markNoQuote: (opp) => setTarget({ opp, status: 'No Quote' }),
    modals,
  };
}

export default useCloseOpportunity;
